import { colors } from '@/theme/tokens/colors';
import type { PaletteMode } from '@/ui/primitives';

type BuildClerkAppearanceParams = {
  mode: PaletteMode;
  cardRadius: string;
  controlRadius: string;
  clerkPrimaryColor: string;
  passwordToggleColor: string;
  resendCodeColor: string;
};

const hiddenOrganisationElements = {
  formFieldRow__organizationLogo: { display: 'none' },
  formFieldRow__organizationProfileImage: { display: 'none' },
  formFieldLabel__organizationLogo: { display: 'none' },
  formFieldLabel__organizationProfileImage: { display: 'none' },
  formFieldInput__organizationLogo: { display: 'none' },
  formFieldInput__organizationProfileImage: { display: 'none' },
  formFieldInputGroup__organizationLogo: { display: 'none' },
  formFieldInputGroup__organizationProfileImage: { display: 'none' },
  organizationAvatarUploaderContainer: { display: 'none' },
  organizationPreviewAvatarBox: { display: 'none' },
  organizationPreviewAvatarContainer: { display: 'none' },
  fileDropAreaBox: { display: 'none' },
} as const;

const buildSharedCardElements = ({
  borderColor,
  surfaceColor,
  cardRadius,
}: {
  borderColor: string;
  surfaceColor: string;
  cardRadius: string;
}): Record<string, unknown> => ({
  rootBox: {
    boxShadow: 'none',
    border: `1px solid ${borderColor}`,
    borderRadius: cardRadius,
    overflow: 'hidden',
    backgroundColor: surfaceColor,
  },
  cardBox: {
    boxShadow: 'none',
    border: 'none',
    backgroundColor: surfaceColor,
  },
  card: {
    boxShadow: 'none',
    border: 'none',
    borderRadius: cardRadius,
    backgroundColor: surfaceColor,
  },
  footer: {
    borderTop: 'none',
    backgroundColor: surfaceColor,
    backgroundImage: 'none',
  },
});

const buildSharedUserButtonElements = ({
  cardRadius,
  borderColor,
}: {
  cardRadius: string;
  borderColor: string;
}): Record<string, unknown> => ({
  userButtonTrigger: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    padding: 0,
  },
  userButtonTrigger__open: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  userButtonTrigger__active: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  userButtonBox: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    padding: 0,
  },
  userButtonAvatarBox: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
  },
  avatarBox: {
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
  },
  userButtonPopoverCard: {
    borderRadius: cardRadius,
    border: `1px solid ${borderColor}`,
    width: '320px',
    maxWidth: 'calc(100vw - 24px)',
    minWidth: 'min(320px, calc(100vw - 24px))',
  },
  userButtonPopoverRootBox: {
    borderRadius: cardRadius,
    width: '320px',
    maxWidth: 'calc(100vw - 24px)',
    minWidth: 'min(320px, calc(100vw - 24px))',
  },
  userButtonPopoverMain: {
    borderRadius: `${cardRadius} ${cardRadius} 0 0`,
  },
  userButtonPopoverFooter: {
    borderRadius: `0 0 ${cardRadius} ${cardRadius}`,
  },
});

export const buildClerkAppearance = ({
  mode,
  cardRadius,
  controlRadius,
  clerkPrimaryColor,
  passwordToggleColor,
  resendCodeColor,
}: BuildClerkAppearanceParams): Record<string, unknown> => {
  if (mode === 'dark') {
    return {
      variables: {
        borderRadius: controlRadius,
        colorBackground: colors.dark.surface,
        colorInputBackground: colors.dark.surfaceAlt,
        colorInputText: '#f2f2f2',
        colorText: '#f2f2f2',
        colorTextSecondary: '#cfd8dc',
        colorPrimary: clerkPrimaryColor,
        colorDanger: '#ff6b6b',
        colorNeutral: colors.dark.bg,
      },
      elements: {
        ...buildSharedCardElements({
          borderColor: colors.dark.border,
          surfaceColor: colors.dark.surface,
          cardRadius,
        }),
        socialButtonsBlockButton: {
          backgroundColor: '#ffffff',
          color: '#111111',
          border: '1px solid #d9d9d9',
          borderRadius: controlRadius,
        },
        socialButtonsBlockButtonText: {
          color: '#111111',
        },
        socialButtonsBlockButtonArrow: {
          color: '#111111',
        },
        formButtonPrimary: {
          borderRadius: controlRadius,
        },
        formFieldInput: {
          borderRadius: controlRadius,
          border: `1px solid ${colors.dark.border}`,
          boxShadow: 'none',
          outline: 'none',
        },
        formFieldInput__hover: {
          borderColor: colors.dark.border,
          boxShadow: 'none',
          outline: 'none',
        },
        formFieldInput__focus: {
          borderColor: clerkPrimaryColor,
          boxShadow: 'none',
          outline: 'none',
        },
        formFieldInput__focusWithin: {
          borderColor: clerkPrimaryColor,
          boxShadow: 'none',
          outline: 'none',
        },
        otpCodeFieldInput: {
          borderRadius: controlRadius,
          borderColor: 'rgba(255, 255, 255, 0.32)',
          borderWidth: '1px',
          borderStyle: 'solid',
          backgroundColor: '#303236',
          boxShadow: 'none',
        },
        otpCodeFieldInput__focus: {
          borderColor: clerkPrimaryColor,
          boxShadow: 'none',
        },
        otpCodeFieldInput__hover: {
          borderColor: 'rgba(255, 255, 255, 0.42)',
        },
        input: {
          borderRadius: controlRadius,
          border: '1px solid transparent',
          boxShadow: 'none',
          outline: 'none',
        },
        input__focus: {
          borderColor: clerkPrimaryColor,
          boxShadow: 'none',
          outline: 'none',
        },
        input__focusVisible: {
          borderColor: clerkPrimaryColor,
          boxShadow: 'none',
          outline: 'none',
        },
        otpCodeFieldInputs: {
          gap: '8px',
        },
        formFieldInputShowPasswordButton: {
          color: passwordToggleColor,
        },
        formFieldInputShowPasswordIcon: {
          color: passwordToggleColor,
        },
        formResendCodeLink: {
          color: resendCodeColor,
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          marginTop: '10px',
          display: 'inline-block',
        },
        ...hiddenOrganisationElements,
        userButtonPopoverActionButtonText: {
          color: '#f2f2f2',
          fontSize: '1rem',
          fontWeight: 400,
          lineHeight: '1.5rem',
          opacity: 1,
        },
        userButtonPopoverActionButtonIcon: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButton: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButton__hover: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButton__manageAccount: {
          color: '#f2f2f2',
          opacity: 1,
          display: 'none',
        },
        userButtonPopoverActionButton__manageAccount__hover: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButton__signOut: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButton__signOut__hover: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButtonText__hover: {
          color: '#f2f2f2',
          opacity: 1,
        },
        userButtonPopoverActionButtonIcon__hover: {
          color: '#f2f2f2',
          opacity: 1,
        },
        ...buildSharedUserButtonElements({
          cardRadius,
          borderColor: colors.dark.border,
        }),
      },
    };
  }

  return {
    variables: {
      borderRadius: controlRadius,
      colorBackground: colors.light.surface,
      colorInputBackground: colors.light.surfaceAlt,
      colorInputText: colors.light.text,
      colorText: colors.light.text,
      colorTextSecondary: colors.light.textMuted,
      colorPrimary: clerkPrimaryColor,
      colorNeutral: colors.light.bg,
    },
    elements: {
      ...buildSharedCardElements({
        borderColor: colors.light.border,
        surfaceColor: colors.light.surface,
        cardRadius,
      }),
      formButtonPrimary: {
        borderRadius: controlRadius,
      },
      formFieldInput: {
        borderRadius: controlRadius,
        border: `1px solid ${colors.light.border}`,
        boxShadow: 'none',
        outline: 'none',
      },
      formFieldInput__hover: {
        borderColor: colors.light.border,
        boxShadow: 'none',
        outline: 'none',
      },
      formFieldInput__focus: {
        borderColor: clerkPrimaryColor,
        boxShadow: 'none',
        outline: 'none',
      },
      formFieldInput__focusWithin: {
        borderColor: clerkPrimaryColor,
        boxShadow: 'none',
        outline: 'none',
      },
      otpCodeFieldInput: {
        borderRadius: controlRadius,
        borderColor: colors.light.border,
        borderWidth: '1px',
        borderStyle: 'solid',
        backgroundColor: colors.light.surfaceAlt,
        boxShadow: 'none',
      },
      otpCodeFieldInput__focus: {
        borderColor: clerkPrimaryColor,
        boxShadow: 'none',
      },
      otpCodeFieldInput__hover: {
        borderColor: colors.light.border,
      },
      input: {
        borderRadius: controlRadius,
        border: '1px solid transparent',
        boxShadow: 'none',
        outline: 'none',
      },
      input__focus: {
        borderColor: clerkPrimaryColor,
        boxShadow: 'none',
        outline: 'none',
      },
      input__focusVisible: {
        borderColor: clerkPrimaryColor,
        boxShadow: 'none',
        outline: 'none',
      },
      otpCodeFieldInputs: {
        gap: '8px',
      },
      formFieldInputShowPasswordButton: {
        color: passwordToggleColor,
      },
      formFieldInputShowPasswordIcon: {
        color: passwordToggleColor,
      },
      formResendCodeLink: {
        color: resendCodeColor,
        fontWeight: 400,
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
      },
      ...hiddenOrganisationElements,
      ...buildSharedUserButtonElements({
        cardRadius,
        borderColor: colors.light.border,
      }),
      userButtonPopoverActionButton__manageAccount: {
        display: 'none',
      },
    },
  };
};
